import { CalendarDate, formatCurrency } from "@sp-crm/core";
import { defaultCardStyle } from "components/ui/card";
import { CalendarDateInput } from "components/ui/calendar-date";
import { Checkbox } from "components/ui/checkbox";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store/state";
import {
    ClientPlacementRevenueQuery,
    useClientPlacementRevenueQuery,
} from "../../../generated/graphql";
import { Actions } from "../../../store/actions";
import { Error } from "../../error";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";

type Segment =
    ClientPlacementRevenueQuery["reportClientPlacementRevenue"]["segments"][number];

export const ClientPlacementRevenue: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const endDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_END_DATE_UPDATE],
            value: newdate,
        });
    };

    const startDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_START_DATE_UPDATE],
            value: newdate,
        });
    };

    const showPerUserOnChangeHandler = (newVal: boolean) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_SHOW_PER_USER_UPDATE],
            value: newVal,
        });
    };

    const startDate = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.startDate,
    );

    const endDate = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.endDate,
    );

    const userPivot = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.showPerUser,
    );

    const region = useSelector((state: ApplicationState) => state.region.selectedRegion);

    const validQuery = region && region !== "" && startDate && endDate;

    return (
        <Stage>
            <PageHeader title="Client Placements">
                <div className="max-w-lg lg:max-w-none lg:flex justify-between lg:space-x-3 items-end">
                    <Checkbox
                        label="Show per user"
                        checked={userPivot}
                        onChange={e => showPerUserOnChangeHandler(e.target.checked)}
                    />
                    <CalendarDateInput
                        label="Start"
                        value={startDate}
                        onChange={startDateOnChangeHandler}
                    />
                    <CalendarDateInput
                        label="End"
                        value={endDate}
                        onChange={endDateOnChangeHandler}
                    />
                </div>
            </PageHeader>
            <ContentContainer>
                {validQuery ? (
                    <RevenueReport
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        region={region}
                        userPivot={userPivot}
                    />
                ) : null}
                <div className="px-2 lg:px-4 py-2">
                    <em className="text-sm text-gray-500">
                        Move counts are shown based on the clients&apos;{" "}
                        <strong>Move Date</strong>, and revenue shown is based on the{" "}
                        <strong>Invoice Date</strong>.
                    </em>
                </div>
            </ContentContainer>
        </Stage>
    );
};

const RevenueReport: React.FC<{
    startDate: string;
    endDate: string;
    userPivot: boolean;
    region: string;
}> = props => {
    const queryResult = useClientPlacementRevenueQuery({
        startDate: props.startDate,
        endDate: props.endDate,
        region: props.region,
    });

    if (queryResult.isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (queryResult.error) {
        return (
            <ContentFull>
                <Error componentName="ClientPlacementRevenue">
                    {(queryResult.error as { message?: string })?.message ??
                        "An error occurred"}
                </Error>
            </ContentFull>
        );
    }

    const segments = queryResult.data?.reportClientPlacementRevenue?.segments ?? [];

    const totalMoves = segments.reduce((acc, s) => acc + s.moves, 0);
    const totalRevenue = segments.reduce((acc, s) => acc + s.revenue, 0);

    return (
        <div className="space-y-2 lg:space-y-4">
            <div className="grid grid-cols-2 gap-2 lg:gap-4">
                <ContentFull>
                    <div className="text-sm font-medium text-gray-500">Total Moves</div>
                    <div className="text-2xl font-semibold text-zinc-950">
                        {totalMoves}
                    </div>
                </ContentFull>
                <ContentFull>
                    <div className="text-sm font-medium text-gray-500">Total Revenue</div>
                    <div className="text-2xl font-semibold text-zinc-950">
                        {formatCurrency(totalRevenue)}
                    </div>
                </ContentFull>
            </div>
            <div className={`w-full overflow-hidden ${defaultCardStyle}`}>
                {props.userPivot ? (
                    <PerUserTable
                        segments={segments}
                        totalMoves={totalMoves}
                        totalRevenue={totalRevenue}
                    />
                ) : (
                    <SummaryTable
                        segments={segments}
                        totalMoves={totalMoves}
                        totalRevenue={totalRevenue}
                    />
                )}
            </div>
        </div>
    );
};

const SummaryTable: React.FC<{
    segments: Segment[];
    totalMoves: number;
    totalRevenue: number;
}> = ({ segments, totalMoves, totalRevenue }) => {
    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        Month
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Moves
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Revenue
                    </th>
                </tr>
            </thead>
            <tbody>
                {segments.map((segment, i) => {
                    const calendarDate = CalendarDate.parse(segment.startDate).getOrElse(
                        CalendarDate.epoch,
                    );
                    return (
                        <tr
                            key={segment.startDate}
                            className={`border-b border-gray-200 hover:bg-gray-100 ${
                                i % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}>
                            <td className="py-2 px-3">
                                {calendarDate.toMonthAndYearString()}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums">
                                {segment.moves}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums">
                                {formatCurrency(segment.revenue)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="bg-gray-100 font-semibold border-t border-gray-300">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right tabular-nums">{totalMoves}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(totalRevenue)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

const PerUserTable: React.FC<{
    segments: Segment[];
    totalMoves: number;
    totalRevenue: number;
}> = ({ segments, totalMoves, totalRevenue }) => {
    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        Month
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        User
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Moves
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Revenue
                    </th>
                </tr>
            </thead>
            <tbody>
                {segments.map(segment => {
                    const calendarDate = CalendarDate.parse(segment.startDate).getOrElse(
                        CalendarDate.epoch,
                    );
                    const monthLabel = calendarDate.toMonthAndYearString();

                    return (
                        <React.Fragment key={segment.startDate}>
                            <tr className="bg-gray-100 border-b border-gray-200">
                                <td className="py-2 px-3 font-medium">{monthLabel}</td>
                                <td className="py-2 px-3" />
                                <td className="py-2 px-3 text-right tabular-nums font-medium">
                                    {segment.moves}
                                </td>
                                <td className="py-2 px-3 text-right tabular-nums font-medium">
                                    {formatCurrency(segment.revenue)}
                                </td>
                            </tr>
                            {segment.users.map(userRow => (
                                <tr
                                    key={`${segment.startDate}-${userRow.user?.id ?? "unassigned"}`}
                                    className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-1.5 px-3" />
                                    <td className="py-1.5 px-3 text-sm text-gray-700">
                                        {userRow.user?.displayName ?? "(unassigned)"}
                                    </td>
                                    <td className="py-1.5 px-3 text-right text-sm tabular-nums">
                                        {userRow.moves}
                                    </td>
                                    <td className="py-1.5 px-3 text-right text-sm tabular-nums">
                                        {formatCurrency(userRow.revenue)}
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="bg-gray-200 font-semibold border-t border-gray-300">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3 text-right tabular-nums">{totalMoves}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(totalRevenue)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};
